import { cn } from '@/lib/utils';
import type { BaseButtonProps, ButtonSize, ButtonVariant } from './types';

/**
 * ButtonSystem — web port of the legacy gradient-border button.
 *
 * The RN version used two stacked `expo-linear-gradient` layers (a gradient
 * "border" behind a gradient-filled inner). On web we reproduce it with a
 * 1px-padded outer element carrying the border gradient and an inner element
 * carrying the fill gradient. Gradients are the one place an inline `style` is
 * allowed (genuinely dynamic values); everything else stays on `tw:` classes.
 */

// The design-system tokens themselves, not copies of their values. These feed
// inline gradients, where var() resolves exactly like a literal would — so a
// token change now reaches the app's main button instead of silently skipping
// it, which is what a hand-copied palette guarantees.
const COLORS = {
  bluePrimary: 'var(--tw-color-blue-primary)',
  blue6: 'var(--tw-color-blue-6)',
  dark1: 'var(--tw-color-dark-1)',
  darkPrimary: 'var(--tw-color-dark-primary)',
  dark3: 'var(--tw-color-dark-3)',
  white: 'var(--tw-color-white)',
  grey4: 'var(--tw-color-grey-4)',
} as const;

type GradientSet = {
  border: [string, string];
  fill: [string, string];
  text: string;
};

const VARIANTS: Record<ButtonVariant, { default: GradientSet; disabled: GradientSet }> = {
  primary: {
    default: {
      border: [COLORS.blue6, COLORS.bluePrimary],
      fill: [COLORS.bluePrimary, COLORS.blue6],
      text: COLORS.white,
    },
    disabled: {
      border: [COLORS.dark1, COLORS.darkPrimary],
      fill: [COLORS.dark1, COLORS.darkPrimary],
      text: COLORS.grey4,
    },
  },
  secondary: {
    default: {
      border: [COLORS.blue6, COLORS.bluePrimary],
      fill: [COLORS.dark1, COLORS.darkPrimary],
      text: COLORS.white,
    },
    disabled: {
      border: [COLORS.dark1, COLORS.darkPrimary],
      fill: [COLORS.darkPrimary, COLORS.dark3],
      text: COLORS.grey4,
    },
  },
  off: {
    default: {
      border: [COLORS.darkPrimary, COLORS.blue6],
      fill: [COLORS.blue6, COLORS.darkPrimary],
      text: COLORS.white,
    },
    disabled: {
      border: [COLORS.dark1, COLORS.darkPrimary],
      fill: [COLORS.blue6, COLORS.darkPrimary],
      text: COLORS.white,
    },
  },
};

const SIZES: Record<ButtonSize, { h: string; px: string; text: string }> = {
  default: { h: 'tw:h-14', px: 'tw:px-5', text: 'tw:text-base' },
  s: { h: 'tw:h-11', px: 'tw:px-4', text: 'tw:text-[15px]' },
  xs: { h: 'tw:h-9', px: 'tw:px-3', text: 'tw:text-[13px]' },
};

const gradient = ([from, to]: [string, string]) =>
  `linear-gradient(180deg, ${from} 0%, ${to} 100%)`;

const ButtonSystem = ({
  label,
  onClick,
  leftIcon,
  rightIcon,
  disabled = false,
  loading = false,
  className,
  size = 'default',
  variant = 'primary',
  type = 'button',
  ...rest
}: BaseButtonProps) => {
  const isInactive = disabled || loading;
  const colors = isInactive ? VARIANTS[variant].disabled : VARIANTS[variant].default;
  const dims = SIZES[size];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isInactive}
      data-testid={rest['data-testid']}
      className={cn(
        'tw:relative tw:w-full tw:overflow-hidden tw:rounded-2xl tw:p-px',
        'tw:transition-opacity tw:duration-150 tw:active:opacity-80',
        isInactive && 'tw:opacity-75',
        className,
      )}
      style={{ background: gradient(colors.border) }}
    >
      <span
        className={cn(
          'tw:flex tw:w-full tw:items-center tw:justify-center tw:gap-2 tw:rounded-[15px]',
          dims.h,
          dims.px,
        )}
        style={{ background: gradient(colors.fill) }}
      >
        {loading ? (
          <span
            className="tw:size-5 tw:animate-spin tw:rounded-full tw:border-2 tw:border-white/30 tw:border-t-white"
            aria-label="loading"
          />
        ) : (
          <>
            {leftIcon && <span className="tw:flex tw:shrink-0 tw:items-center">{leftIcon}</span>}
            {label && (
              <span className={cn('tw:font-semibold', dims.text)} style={{ color: colors.text }}>
                {label}
              </span>
            )}
            {rightIcon && <span className="tw:flex tw:shrink-0 tw:items-center">{rightIcon}</span>}
          </>
        )}
      </span>
    </button>
  );
};

export default ButtonSystem;
